import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { SINGLE_QUOTES_REGEX } from '@common/constants';
import { BooleanOperator, ComparisonOperator, MailFolderType } from '@common/enums';
import {
  Mail,
  MailAttachment,
  MailAttachmentConfigOData,
  MailFolder,
  OdataObject,
  RestoreMailFolder,
  RestoreToAnotherUser
} from '@common/models';
import { ODataPagedResult } from '@common/odata';
import { SharedOdataService } from '@common/services';
import { MailTag, MailTagsMap, SizeFilter } from '@common/services/smart-search';
import { FilterOptions } from '@common/types';
import { filterByWords, getOdataTop } from '@common/utils';
import { containsWrapper, getFilterByContains, getFilterByDateRange, getFilterByEq, getFilterBySize } from '@common/utils/functions/search';
import { IKeyValue } from 'linq-collections';
import { SmartSearchModel, SmartSearchModelField } from 'mbs-ui-kit';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class MailOdataService extends SharedOdataService<Mail> {
  private readonly http = inject(HttpClient);
  private readonly odataMethods = this.odataFactory.CreateService<any>('');
  private readonly mailMessagesService = this.odataFactory.CreateService<any>('MailMessages');

  #customOptions: IKeyValue<string, any> | IKeyValue<string, any>[];

  readonly #backupArchive = MailTagsMap.get(MailTag.BackupArchive);
  readonly #dateFrom = MailTagsMap.get(MailTag.DateFrom);
  readonly #dateTo = MailTagsMap.get(MailTag.DateTo);
  readonly #from = MailTagsMap.get(MailTag.From);
  readonly #sizeLess = MailTagsMap.get(MailTag.SizeLess);
  readonly #sizeMore = MailTagsMap.get(MailTag.SizeMore);
  readonly #subject = MailTagsMap.get(MailTag.Subject);
  readonly #to = MailTagsMap.get(MailTag.To);
  readonly #withAttachment = MailTagsMap.get(MailTag.WithAttachment);

  public accessor groupId = '';
  public accessor userId = '';

  constructor() {
    super('MailFolders');
  }

  isAvailableArchiveOptions(): Observable<boolean> {
    const newOdata = this.odataFactory.CreateService<Mail>(`/MailFolders(${this.groupId})/MailMessages`);

    return newOdata
      .Query()
      .Filter(`IsArchived ${ComparisonOperator.Eq} true`)
      .Top(1)
      .Exec()
      .pipe(map((mails) => mails?.length > 0));
  }

  getMailsByGroupId(): Observable<ODataPagedResult<Mail>> {
    const newOdata = this.odataFactory.CreateService<Mail>(`/MailFolders(${this.groupId})/MailMessages`);

    this.#customOptions = [{ key: 'UserId', value: this.userId }];

    return newOdata
      .Query()
      .CustomQueryOptions(this.#customOptions)
      .OrderBy(this.orderBy)
      .Filter(this.filter)
      .Top(this.pageSize)
      .Skip(this.pageSize * (this.page - 1))
      .ExecWithCount();
  }

  getMailFolders(userId: string, options?: { inbox?: boolean; search?: string; skip?: number }): Observable<ODataPagedResult<MailFolder>> {
    const { inbox, search, skip } = options || {};
    const filter = inbox
      ? `Type ${ComparisonOperator.Eq} '${MailFolderType.Inbox}'`
      : `Type ${ComparisonOperator.Ne} '${MailFolderType.Inbox}'`;

    return this.odataFactory
      .CreateService<MailFolder>(`/BackupUsers(${userId})/MailFolders`)
      .Query()
      .OrderBy(inbox ? null : 'Name asc')
      .Top(getOdataTop(search || inbox))
      .Skip(skip)
      .Filter(search ? containsWrapper('Name', search) : filter)
      .ExecWithCount();
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  updateFilter(searchObj: SmartSearchModel): void {
    if (this.groupId == null) return;

    const filter: string[] = [];
    this.#customOptions = null;

    if (searchObj[this.#from.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#from.tag] as SmartSearchModelField[],
        prop: this.#from.prop
      };

      filter.push(getFilterByContains(options));
    }

    if (searchObj[this.#to.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#to.tag] as SmartSearchModelField[],
        prop: this.#to.prop
      };

      filter.push(getFilterByContains(options));
    }

    if (searchObj[this.#subject.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#subject.tag] as SmartSearchModelField[],
        prop: this.#subject.prop
      };

      filter.push(getFilterByContains(options));
    }

    if (searchObj[this.#withAttachment.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#withAttachment.tag] as SmartSearchModelField[],
        prop: this.#withAttachment.prop
      };

      filter.push(getFilterByEq(options, false));
    }

    if (searchObj[this.#backupArchive.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#backupArchive.tag] as SmartSearchModelField[],
        prop: this.#backupArchive.prop
      };

      filter.push(getFilterByEq(options, false));
    }

    if (searchObj[this.#sizeMore.tag]) {
      const options: FilterOptions & { filter: SizeFilter } = {
        model: searchObj[this.#sizeMore.tag] as SmartSearchModelField[],
        prop: this.#sizeMore.prop,
        filter: SizeFilter.More
      };

      filter.push(getFilterBySize(options));
    }

    if (searchObj[this.#sizeLess.tag]) {
      const options: FilterOptions & { filter: SizeFilter } = {
        model: searchObj[this.#sizeLess.tag] as SmartSearchModelField[],
        prop: this.#sizeLess.prop,
        filter: SizeFilter.Less
      };

      filter.push(getFilterBySize(options));
    }

    if (searchObj[this.#dateFrom.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#dateFrom.tag] as SmartSearchModelField[],
        prop: this.#dateFrom.prop
      };
      const value = getFilterByDateRange(options, 'from');

      value && filter.push(value);
    }

    if (searchObj[this.#dateTo.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#dateTo.tag] as SmartSearchModelField[],
        prop: this.#dateTo.prop
      };
      const value = getFilterByDateRange(options, 'to');

      value && filter.push(value);
    }

    if (searchObj.words?.filter(Boolean)) {
      const term = filterByWords(searchObj).replace(SINGLE_QUOTES_REGEX, '');
      const keys = [this.#subject.prop, this.#from.prop, this.#to.prop];
      const values = keys.map((k) => `(${containsWrapper(k, term)})`);

      filter.push('('.concat(values.join(` ${BooleanOperator.Or} `), ')'));
    }

    this.filter = filter.length > 0 ? filter.join(` ${BooleanOperator.And} `) : '';
  }

  getMailBodyUrl(id: string): Observable<string> {
    const url = this.mailMessagesService.Get(id).GetUrl() + '/MsgBody';

    return this.http.get(url, { responseType: 'blob' }).pipe(
      map((response) => {
        // change blob content type
        response = response.slice(0, response.size, 'text/html');
        return URL.createObjectURL(response);
      })
    );
  }

  getMailAttachment(mailId: string): Observable<MailAttachment[]> {
    return this.mailMessagesService.ItemProperty<OdataObject<MailAttachmentConfigOData[]>>(mailId, 'GetAttachments').pipe(
      map((result) => result.value),
      map((result) => result.map((a) => new MailAttachment(a.Name, a.MsgId, a.Num == null ? null : `?num=${a.Num}`)))
    );
  }

  restoreAllMailbox(userId: string, restoreTo: RestoreToAnotherUser = null): Observable<any> {
    return this.odataMethods.CustomCollectionAction('RestoreWholeMail', { UserId: userId, AnotherUser: restoreTo });
  }

  restoreFolder(options: { folderId: string; restoreTo?: RestoreToAnotherUser; subFolders?: boolean }): Observable<any> {
    const { folderId, restoreTo, subFolders } = options || {};
    const postData: RestoreMailFolder = {
      FolderId: folderId,
      AnotherUser: restoreTo || null
    };

    if (subFolders != null) {
      postData.IncludeSubfolders = subFolders;
    }

    return this.odataMethods.CustomCollectionAction('RestoreMailFolder', postData);
  }

  restoreMessages(messageIds: string[], restoreTo: RestoreToAnotherUser = null): Observable<any> {
    return this.odataMethods.CustomCollectionAction('RestoreMailMsgs', { MsgsIds: messageIds, AnotherUser: restoreTo });
  }

  /*
   * Get top 20 emails in 'From' section that match `containsValue`
   * @param {string} containsValue search string
   */
  getEmailsInFromSection(containsValue: string): Observable<Mail[]> {
    return this.searchItems(MailTag.From, containsValue);
  }

  /*
   * Get top 20 emails in 'To' section that match `containsValue`
   * @param {string} containsValue search string
   */
  getEmailsInToSection(containsValue: string): Observable<Mail[]> {
    return this.searchItems(MailTag.To, containsValue);
  }

  /*
   * Get top 20 subjects that match `containsValue`
   * @param {string} containsValue search string
   */
  getListOfSubjects(containsValue: string): Observable<Mail[]> {
    return this.searchItems(MailTag.Subject, containsValue);
  }

  private searchItems(searchFields: MailTag.From | MailTag.To | MailTag.Subject, containsValue: string): Observable<Mail[]> {
    this.#customOptions = [{ key: 'UserId', value: this.userId }];

    return this.odataFactory
      .CreateService<Mail>(`/MailFolders(${this.groupId})/MailMessages`)
      .Query()
      .CustomQueryOptions(this.#customOptions)
      .Top(100)
      .Select(searchFields)
      .Filter(containsWrapper(searchFields, containsValue))
      .Exec();
  }

  deleteFolder(folderId: string, password: string): Observable<any> {
    const options = {
      FolderId: folderId,
      Password: password
    };

    return this.odataMethods.CustomCollectionAction('DeleteMailFolder', options);
  }

  deleteMessages(messageIds: string[], password: string): Observable<any> {
    const options = {
      MsgsIds: messageIds,
      Password: password
    };

    return this.odataMethods.CustomCollectionAction('DeleteMailMsgs', options);
  }

  getFolder(userId: string, search?: string): Observable<string[]> {
    const odataService = this.odataFactory.CreateService<{ Name: string }>(`/BackupUsers(${userId})/GetFolders`);

    return odataService
      .Query()
      .Top(20)
      .Filter(containsWrapper('Name', search))
      .Exec()
      .pipe(
        map((folders) => folders.map((f) => f.Name)),
        startWith([])
      );
  }
}
